/**
 * Admin Billing & Finance Module
 * MRR/ARR analytics, subscriptions, GST tracking, Stripe integration
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BACKEND_URL } from '../../config';
import toast from '../../utils/toast';

import {
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  CurrencyDollar,
  DownloadSimple,
  FileText,
  Funnel,
  Globe,
  MagnifyingGlass,
  MapPin,
  Shield,
  TrendUp,
  UsersThree,
  WarningCircle,
  XCircle
} from '@phosphor-icons/react';
// Export Button Component - MUST be defined before RevenueAndTaxesTab
const ExportButton = ({ label, period, format, isCustom }) => {
  const [loading, setLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const handleExport = async () => {
    if (isCustom && !showDatePicker) {
      setShowDatePicker(true);
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      
      if (isCustom && dateRange.start && dateRange.end) {
        params.append('start_date', dateRange.start);
        params.append('end_date', dateRange.end);
      } else {
        params.append('period', period);
      }
      
      const response = await fetch(
        `${BACKEND_URL}/api/admin/billing/gst-export?${params.toString()}`,
        {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const filename = `gst_export_${period}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('GST Export', 'Export downloaded successfully');
        setShowDatePicker(false);
      } else {
        const error = await response.json();
        toast.error('Export Failed', error.detail || 'Failed to generate export');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export Failed', 'An error occurred while generating the export');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleExport}
        disabled={loading}
        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-lg text-white text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <DownloadSimple size={16} />
        )}
        {label}
      </button>
      
      {isCustom && showDatePicker && (
        <div className="absolute top-12 left-0 bg-[#1A1A2E] border border-white/20 rounded-lg p-4 z-10 shadow-xl">
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleExport}
                disabled={!dateRange.start || !dateRange.end || loading}
                className="flex-1 px-3 py-2 bg-[#6366F1] rounded-lg text-white text-sm hover:bg-[#5558E3] disabled:opacity-50"
              >
                Export
              </button>
              <button
                onClick={() => setShowDatePicker(false)}
                className="px-3 py-2 bg-white/10 rounded-lg text-white text-sm hover:bg-white/20"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Revenue & Taxes Tab Component (combines Overview + GST)
const RevenueAndTaxesTab = ({ stats }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="space-y-6"
  >
    {/* Key Metrics */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
            <CurrencyDollar size={24} className="text-white" />
          </div>
          <div>
            <p className="text-xs text-gray-400">MRR</p>
            <p className="text-2xl font-bold text-white">${stats?.mrr?.toLocaleString() || 0}</p>
          </div>
        </div>
        <p className="text-xs text-gray-400">Monthly Recurring Revenue</p>
      </div>

      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
            <TrendUp size={24} className="text-white" />
          </div>
          <div>
            <p className="text-xs text-gray-400">ARR</p>
            <p className="text-2xl font-bold text-white">${stats?.arr?.toLocaleString() || 0}</p>
          </div>
        </div>
        <p className="text-xs text-gray-400">Annual Recurring Revenue</p>
      </div>

      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
            <UsersThree size={24} className="text-white" />
          </div>
          <div>
            <p className="text-xs text-gray-400">Active Subs</p>
            <p className="text-2xl font-bold text-white">
              {stats?.active_subscriptions 
                ? (typeof stats.active_subscriptions === 'object' 
                   ? Object.values(stats.active_subscriptions).reduce((sum, val) => sum + val, 0)
                   : stats.active_subscriptions)
                : 0}
            </p>
          </div>
        </div>
        <p className="text-xs text-gray-400">Active subscriptions</p>
      </div>

      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center">
            <CurrencyDollar size={24} className="text-white" />
          </div>
          <div>
            <p className="text-xs text-gray-400">Avg LTV</p>
            <p className="text-2xl font-bold text-white">${stats?.ltv_average || 0}</p>
          </div>
        </div>
        <p className="text-xs text-gray-400">Lifetime Value</p>
      </div>
    </div>

    {/* Additional Metrics */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <CheckCircle size={18} className="text-green-500" />
          New Subscriptions (30d)
        </h3>
        <p className="text-3xl font-bold text-white">{stats?.new_subscriptions_30d || 0}</p>
      </div>

      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <XCircle size={18} className="text-red-500" />
          Failed Payments (30d)
        </h3>
        <p className="text-3xl font-bold text-white">{stats?.failed_payments_30d || 0}</p>
      </div>

      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Clock size={18} className="text-yellow-500" />
          Upcoming Renewals (7d)
        </h3>
        <p className="text-3xl font-bold text-white">{stats?.upcoming_renewals_7d || 0}</p>
      </div>
    </div>

    {/* GST Section */}
    <div className="mt-8">
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3 mb-6">
        <WarningCircle size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-yellow-500 font-semibold mb-1">GST Auto-Detection for Australian Customers</h3>
          <p className="text-sm text-yellow-200/80">
            The system automatically applies 10% GST to Australian customers based on billing address or IP geolocation.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe size={20} className="text-gray-400" />
            <h3 className="text-white font-semibold">AU Taxable Revenue</h3>
          </div>
          <p className="text-3xl font-bold text-white mb-2">
            ${stats?.au_taxable_revenue?.toLocaleString() || 0}
          </p>
          <p className="text-xs text-gray-400">From Australian customers (30d)</p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <MapPin size={20} className="text-gray-400" />
            <h3 className="text-white font-semibold">GST Collected</h3>
          </div>
          <p className="text-3xl font-bold text-white mb-2">
            ${stats?.au_gst_collected?.toLocaleString() || 0}
          </p>
          <p className="text-xs text-gray-400">10% GST (30d)</p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={20} className="text-gray-400" />
            <h3 className="text-white font-semibold">BAS Ready</h3>
          </div>
          <p className="text-3xl font-bold text-white mb-2">
            <CheckCircle size={32} className="text-green-500" />
          </p>
          <p className="text-xs text-gray-400">Export available below</p>
        </div>
      </div>
      
      {/* GST Export Section */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <DownloadSimple size={20} className="text-[#6366F1]" />
              GST Export for BAS
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              DownloadSimple GST transaction data for your Business Activity Statement
            </p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <ExportButton 
            label="Current Quarter (Excel)" 
            period="quarter" 
            format="excel"
          />
          <ExportButton 
            label="Last 30 Days (Excel)" 
            period="30d" 
            format="excel"
          />
          <ExportButton 
            label="Current Quarter (CSV)" 
            period="quarter" 
            format="csv"
          />
          <ExportButton 
            label="Custom Date Range" 
            period="custom" 
            format="excel"
            isCustom
          />
        </div>
      </div>
    </div>
  </motion.div>
);

// Transactions & Invoices Tab Component
const TransactionsTab = ({ onExport }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    range: '30d',
    country: 'all',
    plan: 'all'
  });

  useEffect(() => {
    fetchTransactions();
  }, [filters]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.range) params.append('range', filters.range);
      if (filters.country !== 'all') params.append('country', filters.country);
      if (filters.plan !== 'all') params.append('plan', filters.plan);

      const response = await fetch(
        `${BACKEND_URL}/api/admin/billing/transactions?${params.toString()}`,
        {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTransactions(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Filters Bar */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <select
            value={filters.range}
            onChange={(e) => setFilters({...filters, range: e.target.value})}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="quarter">This Quarter</option>
            <option value="ytd">Year to Date</option>
          </select>

          <select
            value={filters.country}
            onChange={(e) => setFilters({...filters, country: e.target.value})}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="all">All Countries</option>
            <option value="AU">Australia</option>
            <option value="US">United States</option>
            <option value="OTHER">Other</option>
          </select>

          <select
            value={filters.plan}
            onChange={(e) => setFilters({...filters, plan: e.target.value})}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="all">All Plans</option>
            <option value="pro">Pro</option>
            <option value="partner-pro">Partner Pro</option>
          </select>

          <button
            onClick={() => onExport('transactions')}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#5558E3] rounded-lg transition-colors text-white"
          >
            <DownloadSimple size={16} />
            <span className="text-sm font-medium">Export BAS CSV</span>
          </button>
        </div>
      </div>

      {/* Transactions Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6366F1] mx-auto"></div>
        </div>
      ) : transactions.length > 0 ? (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-white/5 border-b border-white/10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Country</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Plan</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">Amount</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">GST</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {transactions.map((txn) => (
                <tr key={txn.id} className="hover:bg-white/5">
                  <td className="px-6 py-4 text-sm text-white">{new Date(txn.date).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-sm text-white">{txn.customer_name}</td>
                  <td className="px-6 py-4 text-sm text-white">{txn.customer_country}</td>
                  <td className="px-6 py-4 text-sm text-white">{txn.plan}</td>
                  <td className="px-6 py-4 text-sm text-white text-right">${txn.amount}</td>
                  <td className="px-6 py-4 text-sm text-white text-right">
                    {txn.gst_amount ? `$${txn.gst_amount}` : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      txn.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                      txn.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {txn.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 bg-white/5 rounded-xl">
          <CreditCard size={48} className="text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Transactions</h3>
          <p className="text-gray-400 mb-4">Stripe integration not configured yet</p>
          <p className="text-sm text-gray-500">Add STRIPE_SECRET_KEY to .env to enable billing features</p>
        </div>
      )}
    </div>
  );
};

// Plan Defaults & Limits Tab Component
const PlanLimitsTab = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Pro Plan */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
            <Shield size={24} className="text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-lg">Pro Plan</h3>
            <p className="text-sm text-gray-400">Individual users & small teams</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between items-center pb-2 border-b border-white/10">
            <span className="text-gray-400">Max Agents</span>
            <span className="text-white font-semibold">3</span>
          </div>
          <div className="flex justify-between items-center pb-2 border-b border-white/10">
            <span className="text-gray-400">Voice Agents</span>
            <span className="text-white font-semibold">1</span>
          </div>
          <div className="flex justify-between items-center pb-2 border-b border-white/10">
            <span className="text-gray-400">Multi-Tenant</span>
            <span className="text-red-400 font-semibold">No</span>
          </div>
          <div className="flex justify-between items-center pb-2 border-b border-white/10">
            <span className="text-gray-400">API Access</span>
            <span className="text-green-400 font-semibold">Yes</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Custom Integrations</span>
            <span className="text-green-400 font-semibold">Yes</span>
          </div>
        </div>
      </div>

      {/* Partner Pro Plan */}
      <div className="bg-white/5 backdrop-blur-sm border border-purple-500/30 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
            <Shield size={24} className="text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-lg">Partner Pro Plan</h3>
            <p className="text-sm text-gray-400">Agencies & enterprise</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex justify-between items-center pb-2 border-b border-white/10">
            <span className="text-gray-400">Max Agents</span>
            <span className="text-purple-400 font-semibold">Unlimited</span>
          </div>
          <div className="flex justify-between items-center pb-2 border-b border-white/10">
            <span className="text-gray-400">Voice Agents</span>
            <span className="text-purple-400 font-semibold">Unlimited</span>
          </div>
          <div className="flex justify-between items-center pb-2 border-b border-white/10">
            <span className="text-gray-400">Multi-Tenant</span>
            <span className="text-green-400 font-semibold">Yes</span>
          </div>
          <div className="flex justify-between items-center pb-2 border-b border-white/10">
            <span className="text-gray-400">API Access</span>
            <span className="text-green-400 font-semibold">Yes</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-400">White Label</span>
            <span className="text-green-400 font-semibold">Yes</span>
          </div>
        </div>
      </div>
    </div>

    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
      <WarningCircle size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
      <div>
        <h3 className="text-blue-500 font-semibold mb-1">Plan Limits Configuration</h3>
        <p className="text-sm text-blue-200/80">
          These limits are enforced platform-wide. To modify plan defaults, update the tier configuration in the backend.
        </p>
      </div>
    </div>
  </div>
);

// Main BillingFinance Component
const BillingFinance = () => {
  const [activeTab, setActiveTab] = useState('revenue');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBillingStats();
  }, []);

  const fetchBillingStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/admin/billing/stats`,
        {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        // Fallback to mock data if endpoint doesn't exist yet
        setStats({
          mrr: 12450,
          arr: 149400,
          new_subscriptions_30d: 23,
          failed_payments_30d: 3,
          upcoming_renewals_7d: 15,
          refunds_30d: 2,
          ltv_average: 1240,
          gst_collected_30d: 1245,
          gst_rate: 0.10,
          active_subscriptions: 142,
          au_taxable_revenue: 4500,
          au_gst_collected: 450
        });
      }
    } catch (error) {
      console.error('Error fetching billing stats:', error);
      // Use mock data
      setStats({
        mrr: 12450,
        arr: 149400,
        new_subscriptions_30d: 23,
        failed_payments_30d: 3,
        upcoming_renewals_7d: 15,
        refunds_30d: 2,
        ltv_average: 1240,
        gst_collected_30d: 1245,
        gst_rate: 0.10,
        active_subscriptions: 142,
        au_taxable_revenue: 4500,
        au_gst_collected: 450
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (type) => {
    toast.info('Export', `Generating ${type} report...`);
  };

  const tabs = [
    { id: 'revenue', label: 'Revenue & Taxes', icon: CurrencyDollar },
    { id: 'transactions', label: 'Transactions & Invoices', icon: CreditCard },
    { id: 'limits', label: 'Plan Defaults & Limits', icon: FileText },
  ];

  if (loading && !stats) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-white/10 rounded animate-pulse w-64"></div>
        <div className="grid grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-white/10 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Billing & Finance</h1>
          <p className="text-gray-400">Revenue analytics, subscriptions, and tax reporting</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'text-[#6366F1] border-b-2 border-[#6366F1]'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'revenue' && <RevenueAndTaxesTab stats={stats} />}
      {activeTab === 'transactions' && <TransactionsTab onExport={handleExport} />}
      {activeTab === 'limits' && <PlanLimitsTab />}
    </div>
  );
};

export default BillingFinance;
